import React from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  ActivityIndicator,
  StyleSheet,
} from 'react-native';
import type { LaunchSecureDto, PatinfoProfileDto, PatinfoPutRequestDto } from '../../../types/api/patinfo';
import type { AccountPlaDto, AccountSraDto, AdminHtmlAnswerDto, MVarDto } from '../../../types/api';
import type { ClientTouchpointSchedule } from '../../../services/clients/ClientFormBuilder';
import type { ClientEnableFlags } from './ClientEnableFlags';
import { ProfileRowEditor, type ViewReportParams } from './ProfileRowEditor';
import { EDITOR_COLORS, editorText } from '../../../styles/editorTheme';

const DEFAULT_BLOCK_SIZE = '10';

interface ManageClientsResultsListProps {
  profiles: PatinfoProfileDto[];
  isLoading: boolean;
  error: string | null;
  blocknum: string;
  blocksize: string;
  hasMore: boolean;
  enableFlags: ClientEnableFlags;
  onNextPage: () => void;
  onPrevPage: () => void;
  /** Shows a right-aligned "Refresh" link after the table when provided — used by Open Admins
   * (a passive feed, not user-search-driven); Manage Clients doesn't pass this. */
  onRefresh?: () => void;
  onSaveProfile: (payload: PatinfoPutRequestDto) => Promise<void>;
  onSendNow: (profile: PatinfoProfileDto, request: LaunchSecureDto) => Promise<void>;
  onCancelLaunch: (profile: PatinfoProfileDto, request: LaunchSecureDto) => Promise<void>;
  onViewReport?: (params: ViewReportParams) => void;
  expandedProfileId?: string | null;
  sendAssessmentOptions?: AdminHtmlAnswerDto[];
  officeOnlineSraMembers?: AccountSraDto[];
  assessmentOptions?: AccountPlaDto[];
  clientPhiFields?: MVarDto[];
  genderPhiOptions?: AdminHtmlAnswerDto[];
  touchpointSchedules?: ClientTouchpointSchedule[];
}

export const ManageClientsResultsList: React.FC<ManageClientsResultsListProps> = ({
  profiles,
  isLoading,
  error,
  blocknum,
  blocksize,
  hasMore,
  enableFlags,
  onNextPage,
  onPrevPage,
  onRefresh,
  onSaveProfile,
  onSendNow,
  onCancelLaunch,
  onViewReport,
  expandedProfileId = null,
  sendAssessmentOptions = [],
  officeOnlineSraMembers = [],
  assessmentOptions = [],
  clientPhiFields = [],
  genderPhiOptions = [],
  touchpointSchedules = [],
}) => {
  const refreshBar = onRefresh ? (
    <View style={styles.refreshBar}>
      <TouchableOpacity
        onPress={onRefresh}
        disabled={isLoading}
        accessibilityRole="button"
        accessibilityLabel="Refresh"
      >
        <Text style={[styles.refreshLinkText, isLoading && styles.refreshLinkTextDisabled]}>Refresh</Text>
      </TouchableOpacity>
    </View>
  ) : null;

  if (isLoading) {
    return (
      <View style={styles.tableWrap}>
        <View style={styles.centeredState}>
          <ActivityIndicator size="large" color={EDITOR_COLORS.accent} />
          <Text style={styles.loadingText}>Searching clients…</Text>
        </View>
        {refreshBar}
      </View>
    );
  }

  if (error) {
    return (
      <View style={styles.tableWrap}>
        <View style={styles.errorBox}>
          <Text style={styles.errorTitle}>Search failed</Text>
          <Text style={styles.errorMsg}>{error}</Text>
        </View>
        {refreshBar}
      </View>
    );
  }

  if (profiles.length === 0) {
    return (
      <View style={styles.tableWrap}>
        <View style={styles.centeredState}>
          <Text style={styles.emptyText}>No clients found. Adjust your search and try again.</Text>
        </View>
        {refreshBar}
      </View>
    );
  }

  const pageNum = parseInt(blocknum, 10);
  const pageSize = parseInt(blocksize || DEFAULT_BLOCK_SIZE, 10);
  const startRow = pageNum * pageSize + 1;
  const endRow = startRow + profiles.length - 1;

  return (
    <View style={styles.tableWrap}>

      <View style={styles.list}>
        <View style={styles.headerRow}>
          <View style={styles.toggleCol}>
            <Text style={styles.headerText}></Text>
          </View>
          <View style={styles.firstNameCol}>
            <Text style={styles.headerText}>First Name</Text>
          </View>
          <View style={styles.lastNameCol}>
            <Text style={styles.headerText}>Last Name</Text>
          </View>
          {enableFlags.enable_idmgmt ? (
            <View style={styles.idCol}>
              <Text style={styles.headerText}>ID Number</Text>
            </View>
          ) : null}
          <View style={enableFlags.enable_idmgmt ? styles.nextCol : styles.nextColNoId}>
            <Text style={styles.headerText}>Next</Text>
          </View>
          <View style={enableFlags.enable_idmgmt ? styles.lastReportCol : styles.lastReportColNoId}>
            <Text style={styles.headerText}>Last Report</Text>
          </View>
          <View style={enableFlags.enable_idmgmt ? styles.viewReportCol : styles.viewReportColNoId}>
            <Text style={styles.headerText}>View Report</Text>
          </View>
        </View>

        {profiles.map(profile => {
          const profileId = profile.patcode || profile.patientid || null;
          return (
            <ProfileRowEditor
              key={profileId}
              profile={profile}
              onSave={onSaveProfile}
              onSendNow={onSendNow}
              onCancelLaunch={onCancelLaunch}
              onViewReport={onViewReport}
              enableFlags={enableFlags}
              initiallyExpanded={!!expandedProfileId && profileId === expandedProfileId}
              sendAssessmentOptions={sendAssessmentOptions}
              officeOnlineSraMembers={officeOnlineSraMembers}
              assessmentOptions={assessmentOptions}
              clientPhiFields={clientPhiFields}
              genderPhiOptions={genderPhiOptions}
              touchpointSchedules={touchpointSchedules}
            />
          );
        })}
      </View>

      {refreshBar}

      <View style={styles.pagination}>
        <TouchableOpacity
          onPress={onPrevPage}
          disabled={pageNum === 0}
          style={[styles.pageBtn, pageNum === 0 && styles.pageBtnDisabled]}
        >
          <Text style={[styles.pageBtnText, pageNum === 0 && styles.pageBtnTextDisabled]}>← Prev</Text>
        </TouchableOpacity>

        <Text style={styles.pageNum}>Page {pageNum + 1}</Text>

        <TouchableOpacity
          onPress={onNextPage}
          disabled={!hasMore}
          style={[styles.pageBtn, !hasMore && styles.pageBtnDisabled]}
        >
          <Text style={[styles.pageBtnText, !hasMore && styles.pageBtnTextDisabled]}>Next →</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  refreshBar: {
    width: '100%',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    paddingTop: 8,
  },
  refreshLinkText: {
    ...editorText,
    color: EDITOR_COLORS.accent,
  },
  refreshLinkTextDisabled: {
    color: EDITOR_COLORS.placeholder,
  },
  tableWrap: {
    width: '100%',
    maxWidth: 850,
    alignSelf: 'center',
  },
  centeredState: {
    paddingVertical: 40,
    alignItems: 'center',
    gap: 10,
  },
  loadingText: {
    ...editorText,
    color: EDITOR_COLORS.textMuted,
  },
  emptyText: {
    ...editorText,
    color: EDITOR_COLORS.textMuted,
    textAlign: 'center',
    maxWidth: 400,
    lineHeight: 20,
  },
  errorBox: {
    backgroundColor: EDITOR_COLORS.errorBackground,
    borderRadius: 12,
    padding: 16,
  },
  errorTitle: {
    ...editorText,
    color: EDITOR_COLORS.errorText,
    marginBottom: 4,
  },
  errorMsg: {
    ...editorText,
    color: EDITOR_COLORS.errorText,
    lineHeight: 19,
  },
  list: {
    width: '100%',
    borderBottomWidth: 1,
    borderBottomColor: EDITOR_COLORS.separator,
  },
  headerRow: {
    minHeight: 40,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: EDITOR_COLORS.background,
    borderBottomWidth: 1,
    borderBottomColor: EDITOR_COLORS.separator,
  },
  headerText: {
    ...editorText,
    color: EDITOR_COLORS.textSecondary,
  },
  toggleCol: {
    width: '8%',
    alignItems: 'center',
    justifyContent: 'center',
  },
  firstNameCol: {
    width: '16%',
    paddingHorizontal: 6,
  },
  lastNameCol: {
    width: '16%',
    paddingHorizontal: 6,
  },
  idCol: {
    width: '15%',
    paddingHorizontal: 6,
  },
  nextCol: {
    width: '14%',
    paddingHorizontal: 6,
  },
  nextColNoId: {
    width: '20%',
    paddingHorizontal: 6,
  },
  lastReportCol: {
    width: '16%',
    paddingHorizontal: 6,
  },
  lastReportColNoId: {
    width: '24%',
    paddingHorizontal: 6,
  },
  viewReportCol: {
    width: '15%',
    paddingHorizontal: 6,
    alignItems: 'center',
  },
  viewReportColNoId: {
    width: '16%',
    paddingHorizontal: 6,
    alignItems: 'center',
  },
  pagination: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 16,
    paddingTop: 20,
  },
  pageBtn: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
    backgroundColor: EDITOR_COLORS.submitBackground,
  },
  pageBtnDisabled: {
    opacity: 0.4,
  },
  pageBtnText: {
    ...editorText,
    color: EDITOR_COLORS.submitText,
  },
  pageBtnTextDisabled: {
    color: EDITOR_COLORS.placeholder,
  },
  pageNum: {
    ...editorText,
    color: EDITOR_COLORS.textSecondary,
  },
});
